import { useMemo, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { PaywallCheckoutContextProvider } from "@/react/contexts/Paywalls/paywallCheckoutContext";
import { loadStripeWrapper } from "@/react/hooks/paywalls/useStripeElement";
import { stripeElementsOptions } from "../StripeElements/stripeElementsOptions";
import { CheckoutPage } from "./CheckoutPage";
import type { CheckoutProps } from "./CheckoutProps";
import { useCheckoutPaywallPrice } from "./hooks/useCheckoutPaywallPrice";
import { useFilterPaymentMethodsSupported } from "./hooks/useFilterPaymentMethodsSupported";
import { useStripeElementOptions } from "./hooks/useStripeElementOptions";

export const CheckoutWithoutPMC = (props: CheckoutProps) => {
  const {
    paywall,
    processorId,
    processorKey,
    paymentMethodsSupported,
    currentCommunityMemberPaymentProcessorSessionClientSecret,
  } = props;

  const paywallPrice = useCheckoutPaywallPrice(props);

  const [disabledPaymentMethods, setDisabledPaymentMethods] = useState<
    string[]
  >([]);

  const filteredPaymentMethodsSupported = useFilterPaymentMethodsSupported(
    paymentMethodsSupported,
    disabledPaymentMethods,
  );

  // Once Login process can be executed at community level we can
  // render this component on App.jsx and start using usePunditUserContext
  // instead of passing current community/user as params
  const stripePromise = useMemo(
    () =>
      loadStripeWrapper({
        stripeAccountId: processorId,
        stripePublicKey: processorKey,
        locale: window.locale,
      }),
    [processorKey, processorId],
  );

  const otherStripeElementsOptions = useStripeElementOptions({
    filteredPaymentMethodsSupported,
    isBuyNowPayLaterAllowed: true,
    paywall,
    price: paywallPrice,
    currentCommunityMemberPaymentProcessorSessionClientSecret,
  });

  return (
    <Elements
      // Force PaymentElement to re-render whenever the Payment Methods supported is changed.
      key={filteredPaymentMethodsSupported.join(",")}
      stripe={stripePromise}
      options={{
        ...stripeElementsOptions,
        ...otherStripeElementsOptions,
      }}
    >
      <PaywallCheckoutContextProvider
        {...props}
        isPaywallsCheckoutCustomizationPhase1Enabled={false}
        paymentMethodsSupported={filteredPaymentMethodsSupported}
        disabledPaymentMethods={disabledPaymentMethods}
        setDisabledPaymentMethods={setDisabledPaymentMethods}
        paymentElementKey=""
      >
        <CheckoutPage />
      </PaywallCheckoutContextProvider>
    </Elements>
  );
};
